// Cart + submission state for the employee request sheet. Lifted out of
// EmployeeApp so the page component stays readable.
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { addRequest, loadRequests, uid } from "../lib/storage";
import { userAreas } from "../lib/permissions";
import { useLanguage } from "../i18n/LanguageContext";

function buildPurchasePayload({ user, supplierId, items, notes, nowIso }) {
  return {
    id: uid("r"),
    type: "purchase",
    restaurant: user.restaurant,
    fromRestaurant: null,
    toRestaurant: null,
    vendor: supplierId,
    supplierType: "external",
    requestedBy: user.id,
    requestedByName: user.name,
    requesterRole: user.role,
    requesterArea: userAreas(user)[0] || null,
    requesterAreas: userAreas(user),
    items,
    status: "submitted",
    notes,
    approvedBy: null,
    approvedAt: null,
    createdAt: nowIso,
    updatedAt: nowIso,
  };
}

export function useEmployeeCart(user, products, onSubmitted) {
  const { t, language } = useLanguage();
  const [cart, setCart] = useState({});
  const [notes, setNotes] = useState("");

  const inc = useCallback((pid) => {
    setCart((c) => ({ ...c, [pid]: { qty: (c[pid]?.qty || 0) + 1, note: c[pid]?.note || "" } }));
  }, []);

  const dec = useCallback((pid) => {
    setCart((c) => {
      const cur = c[pid]?.qty || 0;
      if (cur <= 1) {
        const { [pid]: _drop, ...rest } = c;
        return rest;
      }
      return { ...c, [pid]: { ...c[pid], qty: cur - 1 } };
    });
  }, []);

  const remove = useCallback((pid) => {
    setCart((c) => {
      const { [pid]: _drop, ...rest } = c;
      return rest;
    });
  }, []);

  const setLineNote = useCallback((pid, note) => {
    setCart((c) => ({ ...c, [pid]: { ...c[pid], note } }));
  }, []);

  const reset = useCallback(() => {
    setCart({});
    setNotes("");
  }, []);

  const lines = useMemo(
    () =>
      Object.entries(cart)
        .map(([pid, v]) => ({ product: products.find((p) => p.id === pid), qty: v.qty, note: v.note || "" }))
        .filter((l) => l.product),
    [cart, products]
  );

  const totalCount = useMemo(
    () => Object.values(cart).reduce((a, c) => a + (c.qty || 0), 0),
    [cart]
  );

  const submit = useCallback(() => {
    if (lines.length === 0) {
      toast.error(t("addOne"));
      return;
    }

    const bySupplier = {};
    lines.forEach((l) => {
      const sup = l.product.supplierId;
      bySupplier[sup] = bySupplier[sup] || [];
      bySupplier[sup].push({
        itemId: uid("line"),
        productId: l.product.id,
        productName: l.product.name,
        unit: l.product.unit,
        quantity: l.qty,
        note: l.note,
        purchased: false,
        purchasedQty: 0,
        purchasedBy: null,
        purchasedByName: null,
        purchasedAt: null,
        purchasedSupplier: null,
        purchasedComment: "",
      });
    });

    const nowIso = new Date().toISOString();
    Object.entries(bySupplier).forEach(([supplierId, items]) => {
      addRequest(buildPurchasePayload({ user, supplierId, items, notes, nowIso }));
    });

    const vendorCount = Object.keys(bySupplier).length;
    toast.success(t("sent", { count: vendorCount, plural: language === "es" ? (vendorCount > 1 ? "es" : "") : (vendorCount > 1 ? "s" : "") }));
    reset();
    onSubmitted?.(loadRequests());
  }, [lines, notes, user, reset, onSubmitted, t, language]);

  return {
    cart,
    lines,
    totalCount,
    notes,
    setNotes,
    inc,
    dec,
    remove,
    setLineNote,
    submit,
  };
}
